import type { RootState } from '@app/store/store.js';
import { rpcCall, rpcResponse } from '@features/rpc/model/actions.js';
import { toastsAdd } from '@features/toasts/model/actions.js';
import { wsClose, wsOpen } from '@features/websocket/model/actions.js';
import type { Dispatch, Middleware } from 'redux';
import z from 'zod';
import { loadTrackingMessages } from '../translations/loadTrackingMessages.js';
import type { TrackedDevice } from './types.js';

const TrackingParamsSchema = z.union([
  z.object({ token: z.string() }),
  z.object({ deviceId: z.number() }),
]);

export function createTrackingMiddleware(): Middleware<
  object,
  RootState,
  Dispatch
> {
  let reopenTs: number | undefined;

  return ({ dispatch, getState }) =>
    (next) =>
    (action) => {
      _if: if (rpcResponse.match(action)) {
        const { payload } = action;

        if (
          payload.method !== 'tracking.subscribe' ||
          payload.type !== 'error'
        ) {
          break _if;
        }

        const paramsResult = TrackingParamsSchema.safeParse(payload.params);

        if (!paramsResult.success) {
          break _if;
        }

        const params = paramsResult.data;

        dispatch(
          toastsAdd({
            id: 'tracking.subscribeError',
            messageKey:
              payload.error.code === 404
                ? 'subscribeNotFound'
                : 'subscribeError',
            messageLoader: loadTrackingMessages,
            messageParams: {
              id: 'token' in params ? params.token : params.deviceId, // TODO use different message key
            },
            style: payload.error.code === 404 ? 'warning' : 'danger',
          }),
        );
      }

      const prevState = getState().websocket.state;

      const prevTrackedDevices = getState().tracking.trackedDevices;

      const result = next(action);

      const { trackedDevices } = getState().tracking;

      const { state, timestamp } = getState().websocket;

      if (prevState === state && prevTrackedDevices === trackedDevices) {
        return result;
      }

      if (trackedDevices.length === 0 && reopenTs) {
        clearTimeout(reopenTs);

        reopenTs = undefined;
      }

      if (trackedDevices.length === 0 && state < 2) {
        dispatch(wsClose(null));
      } else if (trackedDevices.length > 0 && state === 3) {
        const diff = Date.now() - timestamp;

        if (diff > 1000) {
          // TODO scale this value
          dispatch(wsOpen(null));
        } else {
          reopenTs = window.setTimeout(() => {
            dispatch(wsOpen(null));
          }, 1000 - diff);
        }
      } else if (prevState !== 1 && state === 1 && trackedDevices.length > 0) {
        for (const td of trackedDevices) {
          dispatch(
            rpcCall({ method: 'tracking.subscribe', params: mangle(td) }),
          );
        }
      } else if (state === 1 && prevTrackedDevices !== trackedDevices) {
        // TODO prevent concurrent subscribe/unsubscribe of the same id and keep their order
        // TODO ignore if only label changed
        for (const td of prevTrackedDevices) {
          if (trackedDevices.includes(td)) {
            continue;
          }

          const { token, deviceId } = mangle(td);

          dispatch(
            rpcCall({
              method: 'tracking.unsubscribe',
              params: { token, deviceId },
            }),
          );
        }

        for (const td of trackedDevices) {
          if (!prevTrackedDevices.includes(td)) {
            dispatch(
              rpcCall({ method: 'tracking.subscribe', params: mangle(td) }),
            );
          }
        }
      }

      return result;
    };
}

function mangle(td: TrackedDevice) {
  const { token: id, ...rest } = td;

  const isDeviceId = /^\d+$/.test(id.toString());

  const deviceId = isDeviceId
    ? typeof id === 'number'
      ? id
      : Number.parseInt(id, 10)
    : id;

  return {
    deviceId: isDeviceId ? deviceId : undefined,
    token: isDeviceId ? undefined : deviceId,
    ...rest,
  };
}
